import type { ThreadListItem, ThreadMessage } from '../types';
import emailApiService from '../services/emailApi.service';
import { useEmailStore } from '../stores/emailStore';

type RawThreadSummary = {
  _id?: string;
  thread_id?: string;
  subject?: string;
  participants?: ThreadListItem['participants'];
  from?: ThreadListItem['last_message_from'];
  last_message_at?: string;
  date?: string;
  last_message_preview?: string;
  text_content?: string;
  folder?: string;
  labels?: string[];
  has_unread?: boolean;
  is_read?: boolean;
  unread_count?: number;
  starred?: boolean;
  flags?: { flagged?: boolean };
  message_count?: number;
  messages?: ThreadMessage[];
  read_by_users?: number[];
};

type ThreadFetchResponse = {
  success?: boolean;
  data?: {
    threads?: RawThreadSummary[];
  };
  threads?: RawThreadSummary[];
};

function normalizeFolderKey(value?: string | null): string {
  return String(value ?? '').trim().toLowerCase();
}

function mapThreadSummary(raw: RawThreadSummary): ThreadListItem | null {
  const threadId = raw.thread_id || raw._id;
  if (!threadId) {
    return null;
  }

  return {
    thread_id: threadId,
    subject: raw.subject || '(No subject)',
    participants: raw.participants || (raw.from ? [raw.from] : []),
    last_message_at: raw.last_message_at || raw.date || new Date().toISOString(),
    last_message_preview: raw.last_message_preview || (raw.text_content ? raw.text_content.substring(0, 200) : ''),
    last_message_from: raw.from,
    folder: raw.folder || 'INBOX',
    labels: raw.labels || [],
    has_unread: raw.has_unread ?? !raw.is_read,
    unread_count: raw.unread_count ?? (raw.is_read ? 0 : 1),
    starred: raw.starred || raw.flags?.flagged || false,
    message_count: raw.message_count || 1,
    messages: raw.messages,
    read_by_users: raw.read_by_users || [],
  };
}

export async function primeThreadFolderCache(folder: string, accountId?: number | null): Promise<void> {
  const store = useEmailStore.getState();
  const targetFolder = folder || store.selectedFolder;
  const requestedAccountId = accountId !== undefined ? accountId : store.activeSharedMailboxId;
  const targetAccountKey = requestedAccountId || 0;

  try {
    const response = await emailApiService.getThreads({
      folder: targetFolder,
      accountId: requestedAccountId || undefined,
      limit: 0,
    });

    const typedResponse = response as ThreadFetchResponse;
    if (!typedResponse.success) {
      return;
    }

    const primedThreads = (typedResponse.threads || typedResponse.data?.threads || [])
      .map(mapThreadSummary)
      .filter((thread): thread is ThreadListItem => thread !== null);

    const latestState = useEmailStore.getState();
    const folderCacheKey = `${targetFolder}:${targetAccountKey}`;
    const isCurrentView =
      !latestState.threadsSearchQuery &&
      normalizeFolderKey(latestState.selectedFolder) === normalizeFolderKey(targetFolder) &&
      (latestState.activeSharedMailboxId || 0) === targetAccountKey;

    useEmailStore.setState((state) => ({
      folderThreads: { ...state.folderThreads, [folderCacheKey]: primedThreads },
      folderThreadsLastSync: { ...state.folderThreadsLastSync, [folderCacheKey]: Date.now() },
      ...(isCurrentView
        ? {
            threads: primedThreads,
            threadsLoading: false,
            threadsCurrentSkip: 0,
            threadsHasMore: false,
            threadsTotalCount: primedThreads.length,
          }
        : {}),
    }));

    if (normalizeFolderKey(targetFolder) === 'inbox') {
      useEmailStore.setState({
        inboxUnreadCount: primedThreads.filter((thread) => thread.has_unread).length,
      });
    }

    if (['spam', 'junk'].includes(normalizeFolderKey(targetFolder))) {
      useEmailStore.setState({
        spamUnreadCount: primedThreads.filter((thread) => thread.has_unread).length,
      });
    }
  } catch (error) {
    console.error('[primeThreadFolderCache] Failed to prime folder cache:', error);
  }
}